import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Build as BuildIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

const EMPTY_FORM = { username: "", password: "", base_url: "" };

export default function BoschCoreIntegrationCard() {
  const { t } = useTranslation();
  const { currentOrganization, fetchUser } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const resp = await axios.get(`/api/bosch-core/integration/?organization=${organizationId}`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to load Bosch CoRe integration", e);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const startEdit = () => {
    setForm({ username: data?.username || "", password: "", base_url: data?.base_url || "" });
    setError("");
    setTestResult(null);
    setEditing(true);
  };

  const handleSave = async () => {
    setBusy(true);
    setError("");
    setTestResult(null);
    try {
      let resp;
      if (data?.configured) {
        // PATCH: credentials only when a new password was typed (they travel as a pair).
        const payload = { base_url: form.base_url };
        if (form.password) {
          payload.username = form.username;
          payload.password = form.password;
        }
        resp = await axios.patch(`/api/bosch-core/integration/?organization=${organizationId}`, payload);
      } else {
        resp = await axios.post(`/api/bosch-core/integration/?organization=${organizationId}`, {
          username: form.username,
          password: form.password,
          base_url: form.base_url,
        });
      }
      setData(resp.data);
      setEditing(false);
      setForm(EMPTY_FORM);
      await fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || t("bosch.settings.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      await axios.post(`/api/bosch-core/integration/test/?organization=${organizationId}`);
      setTestResult({ success: true });
    } catch (err) {
      setTestResult({
        success: false,
        error: err.response?.data?.error || t("bosch.settings.testError"),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    setConfirmRemoveOpen(false);
    try {
      await axios.delete(`/api/bosch-core/integration/?organization=${organizationId}`);
      setEditing(false);
      setForm(EMPTY_FORM);
      setTestResult(null);
      await fetchStatus();
      await fetchUser();
    } catch (err) {
      setError(err.response?.data?.error || t("bosch.settings.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const canSave =
    !busy &&
    (data?.configured
      ? form.password === "" || (form.username && form.password)
      : form.username && form.password);

  return (
    <Card variant="outlined" sx={{ gridColumn: { md: "1 / -1" } }} data-testid="bosch-card">
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <BuildIcon />
          </Box>
          {data?.configured && (
            <Chip size="small" color="success" label={t("Active")} sx={{ ml: "auto" }} />
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {t("bosch.settings.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("bosch.settings.description")}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid="bosch-error">
                {error}
              </Alert>
            )}
            {testResult && (
              <Alert severity={testResult.success ? "success" : "error"} sx={{ mb: 2 }} data-testid="bosch-test-result">
                {testResult.success ? t("bosch.settings.testSuccess") : testResult.error}
              </Alert>
            )}

            {!editing && data?.configured && (
              <>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {t("bosch.settings.connectedAs")}: <code>{data.username}</code>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2, wordBreak: "break-all" }}>
                  {data.effective_base_url}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button variant="outlined" size="small" onClick={handleTest} disabled={busy} data-testid="bosch-test">
                    {busy ? <CircularProgress size={16} /> : t("bosch.settings.testConnection")}
                  </Button>
                  <Button variant="text" size="small" onClick={startEdit} disabled={busy} data-testid="bosch-edit">
                    {t("Edit")}
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={() => setConfirmRemoveOpen(true)}
                    disabled={busy}
                    data-testid="bosch-remove"
                  >
                    {t("bosch.settings.remove")}
                  </Button>
                </Box>
              </>
            )}

            {!editing && !data?.configured && (
              <Button variant="contained" size="small" onClick={startEdit} data-testid="bosch-configure">
                {t("bosch.settings.configure")}
              </Button>
            )}

            {editing && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("bosch.settings.baseUrl")}
                  placeholder={data?.default_base_url || ""}
                  helperText={t("bosch.settings.baseUrlHelp")}
                  value={form.base_url}
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  inputProps={{ "data-testid": "bosch-base-url" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={t("bosch.settings.username")}
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  inputProps={{ "data-testid": "bosch-username" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type={showPassword ? "text" : "password"}
                  label={t("bosch.settings.password")}
                  helperText={data?.configured ? t("bosch.settings.passwordKeepHelp") : ""}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  inputProps={{ "data-testid": "bosch-password" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={!canSave}
                    data-testid="bosch-save"
                  >
                    {busy ? <CircularProgress size={16} /> : t("Save")}
                  </Button>
                  <Button variant="text" size="small" onClick={() => setEditing(false)} disabled={busy}>
                    {t("Cancel")}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}

        <Dialog open={confirmRemoveOpen} onClose={() => setConfirmRemoveOpen(false)}>
          <DialogTitle>{t("bosch.settings.removeConfirmTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("bosch.settings.removeConfirmText")}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRemoveOpen(false)}>{t("Cancel")}</Button>
            <Button color="error" onClick={handleRemove} data-testid="bosch-remove-confirm">
              {t("bosch.settings.remove")}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
